import { useMemo } from 'react';
import { duplicateShot, insertShot, moveShot, newShot, removeShot } from '../../lib/treatmentShots';
import { appendReferences } from '../../lib/treatmentReferences';

/**
 * Every edit the treatment editor makes.
 *
 * Each write uses the functional updater, `setTreatment(prev => ...)`, never a
 * spread of the closure's value: JobEditPage once lost two of three rapid
 * "Add Expense" clicks that way. The shot-list rules themselves live in
 * lib/treatmentShots.js; these handlers only apply them.
 */
export function useTreatmentHandlers(setTreatment) {
  return useMemo(() => {
    const setShots = (fn) => setTreatment((prev) => ({ ...prev, shots: fn(prev.shots || []) }));

    const updateShotWith = (shotId, fn) =>
      setShots((shots) => shots.map((s) => (s.id === shotId ? fn(s) : s)));

    return {
      updateField: (key, value) => setTreatment((prev) => ({ ...prev, [key]: value })),

      /**
       * Adds a setup inside `parentId`'s sequence, or a shot after `afterId`
       * (the selected shot) or at the end. Returns its id.
       */
      addShot: (parentId = null, afterId = null) => {
        const shot = newShot(parentId);
        setShots((shots) => insertShot(shots, shot, afterId));
        return shot.id;
      },

      /** Copies a shot (a sequence with its setups) straight after itself. Returns the copy's id. */
      duplicateShot: (shotId) => {
        const copyId = crypto.randomUUID();
        setShots((shots) => duplicateShot(shots, shotId, copyId).shots);
        return copyId;
      },

      updateShot: (shotId, patch) => updateShotWith(shotId, (s) => ({ ...s, ...patch })),

      removeShot: (shotId) => setShots((shots) => removeShot(shots, shotId)),

      moveShot: (activeId, overId) => setShots((shots) => moveShot(shots, activeId, overId)),

      /** Our own gear, from the inventory. Adding it twice to a shot is a no-op. */
      addInventoryEquipment: (shotId, item) =>
        updateShotWith(shotId, (s) =>
          (s.equipment || []).some((e) => e.inventory_id === item.id)
            ? s
            : {
                ...s,
                equipment: [
                  ...(s.equipment || []),
                  { id: crypto.randomUUID(), inventory_id: item.id, name: item.name, quantity: 1, note: '', supplier_id: null },
                ],
              },
        ),

      /** Something we don't own. Who it comes from is decided later, on sourcing. */
      addHire: (shotId, name) =>
        updateShotWith(shotId, (s) => ({
          ...s,
          equipment: [
            ...(s.equipment || []),
            { id: crypto.randomUUID(), inventory_id: null, name, quantity: 1, note: '', supplier_id: null },
          ],
        })),

      /** Link frames to a shot. Ones it already has are skipped. */
      addReferences: (shotId, urls) =>
        updateShotWith(shotId, (s) => ({ ...s, references: appendReferences(s.references, urls) })),

      updateReference: (shotId, referenceId, patch) =>
        updateShotWith(shotId, (s) => ({
          ...s,
          references: (s.references || []).map((r) => (r.id === referenceId ? { ...r, ...patch } : r)),
        })),

      removeReference: (shotId, referenceId) =>
        updateShotWith(shotId, (s) => ({
          ...s,
          references: (s.references || []).filter((r) => r.id !== referenceId),
        })),

      /** The first frame is the shot's cover in the outline. */
      makeCoverReference: (shotId, referenceId) =>
        updateShotWith(shotId, (s) => {
          const refs = s.references || [];
          const ref = refs.find((r) => r.id === referenceId);
          return ref ? { ...s, references: [ref, ...refs.filter((r) => r !== ref)] } : s;
        }),

      updateEquipment: (shotId, equipmentId, patch) =>
        updateShotWith(shotId, (s) => ({
          ...s,
          equipment: (s.equipment || []).map((e) => (e.id === equipmentId ? { ...e, ...patch } : e)),
        })),

      removeEquipment: (shotId, equipmentId) =>
        updateShotWith(shotId, (s) => ({
          ...s,
          equipment: (s.equipment || []).filter((e) => e.id !== equipmentId),
        })),
    };
  }, [setTreatment]);
}
